import Icon from '../../../components/AppIcon';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import TruckAssignmentModal from './TruckAssignmentModal';
import { ErrorModal } from '../../../components/ui/ErrorModal';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

const SupplySidePendingOrdersTab = () => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [bookingTypeFilter, setBookingTypeFilter] = useState('all');
  const [datePreset, setDatePreset] = useState('till_date');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Partial approval modal state (used for ALL order types)
  const [approvalModal, setApprovalModal] = useState({ open: false, order: null });
  const [approvedQty, setApprovedQty] = useState('');
  const [remainingQuota, setRemainingQuota] = useState(null);
  const [loadingQuota, setLoadingQuota] = useState(false);
  const [qtyError, setQtyError] = useState('');
  const [approving, setApproving] = useState(false);
  const [factoryRate, setFactoryRate] = useState('');
  const [factoryBank, setFactoryBank] = useState('');
  const [factoryUtr, setFactoryUtr] = useState('');

  // Reject modal state
  const [rejectModal, setRejectModal] = useState({ open: false, order: null });
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Available vehicles for Sugar+Truck orders
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [truckInput, setTruckInput] = useState({ vehicleNumber: '', transporterRate: '', capacityMT: '' });
  // Multi-vehicle assignments for multi-truck orders
  const [vehicleAssignments, setVehicleAssignments] = useState([{ vehicle: '', qty: 0, rate: 0 }]);

  // Truck assignment modal (shown AFTER approval for Sugar+Truck orders)
  const [assignModal, setAssignModal] = useState({ open: false, order: null });

  const isSugarTruck = (bt) => bt === 'sugar_truck' || bt === 'sugar_and_truck';
  const loadPendingOrders = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('buyer_bookings')
        ?.select('id, buyer_email, buyer_id, quota_id, offer_id, factory_name, grade, quantity, rate_per_ton, total_amount, status, booking_date, created_at, destination, booking_type, no_of_trucks, readable_offer_id, readable_booking_id, assigned_vehicle_number, tds_applicable, tds_amount, net_payable, do_finance_bid_id')        
        ?.in('status', ['pending', 'pending_approval'])
        ?.order('created_at', { ascending: false });

      if (!error && data) {
        setPendingOrders(data?.map(row => ({
          id: row?.id,
          poNumber: row?.readable_booking_id || row?.readable_offer_id || row?.offer_id || row?.id?.slice(0, 8)?.toUpperCase(),
          buyer: row?.buyer_email || '—',
          factoryName: row?.factory_name || '—',
          sugarGrade: row?.grade || '—',
          qtyRequired: row?.quantity || 0,
          ratePerTon: row?.rate_per_ton || 0,
          totalAmount: row?.total_amount || 0,
          status: row?.status || 'pending',
          requestedDate: row?.booking_date
            ? new Date(row?.booking_date)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : '—',
          submittedAt: row?.booking_date || row?.created_at || '',
          deliveryDestination: row?.destination || '—',
          bookingType: row?.booking_type || 'sugar_only',
          noOfTrucks: row?.no_of_trucks || 0,
          quotaId: row?.quota_id,
          buyerId: row?.buyer_id,
          offerId: row?.offer_id || '',
          readableOfferId: row?.readable_offer_id || null,
          readableBookingId: row?.readable_booking_id || null,
          assignedVehicle: row?.assigned_vehicle_number || '',
          tdsApplicable: row?.tds_applicable || false,
          tdsAmount: row?.tds_amount || 0,
          netPayable: row?.net_payable || row?.total_amount || 0,
          doFinanceBidId: row?.do_finance_bid_id || null,
        })));          
      }
    } catch (err) {
      console.error('Error loading pending orders:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPendingOrders();
    const channel = supabase
      ?.channel('buyer_bookings_pending_tab')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'buyer_bookings' }, () => {
        loadPendingOrders();
      })
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, []);

  // Fetch available vehicles for Sugar+Truck approval modal
  const fetchAvailableVehicles = useCallback(async () => {
    setLoadingVehicles(true);
    const { data, error } = await supabase
      ?.from('trucks')
      ?.select('id, vehicle_number, capacity_mt, rate_per_ton, current_location')
      ?.eq('status', 'Available')
      ?.order('vehicle_number', { ascending: true });
    if (!error && data) setAvailableVehicles(data);
    setLoadingVehicles(false);
  }, []);

  // Calculate remaining quota for a given offer_id + grade
  // Remaining = Factory Quota (original) - Already Approved
  const fetchRemainingQuota = useCallback(async (order) => {
    setLoadingQuota(true);
    setRemainingQuota(null);
    try {
      // 1. Get the CURRENT quota record quantity (already reduced by prior approvals)
      let currentQuotaQty = null;
      let quotaRowId = null;
      if (order?.quotaId) {
        const { data: quotaRow } = await supabase
          ?.from('quotas')
          ?.select('id, quantity, offer_id, grade')
          ?.eq('id', order?.quotaId)
          ?.single();
        if (quotaRow) {
          currentQuotaQty = Number(quotaRow?.quantity);
          quotaRowId = quotaRow?.id;
        }
      }

      // Fallback: match by offer_id in quotas table
      if (currentQuotaQty === null && order?.offerId) {
        const { data: quotaRows } = await supabase
          ?.from('quotas')
          ?.select('id, quantity')
          ?.eq('offer_id', order?.offerId)
          ?.limit(1);
        if (quotaRows?.length > 0) {
          currentQuotaQty = Number(quotaRows?.[0]?.quantity);
          quotaRowId = quotaRows?.[0]?.id;
        }
      }

      // Fallback: match by factory_name + grade
      if (currentQuotaQty === null) {
        const { data: quotaRows } = await supabase
          ?.from('quotas')
          ?.select('id, quantity')
          ?.eq('factory_name', order?.factoryName)
          ?.eq('grade', order?.sugarGrade)
          ?.limit(1);
        if (quotaRows?.length > 0) {
          currentQuotaQty = Number(quotaRows?.[0]?.quantity);
          quotaRowId = quotaRows?.[0]?.id;
        }
      }

      // 2. Sum already-approved quantities for the same offer_id (excluding current order)
      let alreadyApproved = 0;
      if (order?.offerId) {
        const { data: approvedRows } = await supabase
          ?.from('buyer_bookings')
          ?.select('quantity')
          ?.eq('offer_id', order?.offerId)
          ?.in('status', ['approved', 'active', 'confirmed', 'do_generated'])          
          ?.neq('id', order?.id);
        if (approvedRows) {
          alreadyApproved = approvedRows?.reduce((sum, r) => sum + Number(r?.quantity || 0), 0);
        }
      } else {
        // Fallback: match by factory_name + grade
        const { data: approvedRows } = await supabase
          ?.from('buyer_bookings')
          ?.select('quantity')
          ?.eq('factory_name', order?.factoryName)
          ?.eq('grade', order?.sugarGrade)
          ?.in('status', ['approved', 'active', 'confirmed', 'do_generated'])          
          ?.neq('id', order?.id);
        if (approvedRows) {
          alreadyApproved = approvedRows?.reduce((sum, r) => sum + Number(r?.quantity || 0), 0);
        }
      }

      // 3. Factory Quota = current quota qty + already approved (reconstructed original)
      //    Remaining = Factory Quota - Already Approved = current quota qty (live from DB)
      //    This is always consistent: Factory Quota = Already Approved + Remaining
      const factoryQuota = currentQuotaQty !== null ? currentQuotaQty + alreadyApproved : null;
      const remaining = currentQuotaQty !== null ? Math.max(0, currentQuotaQty) : null;

      setRemainingQuota({ available: factoryQuota, alreadyApproved, remaining, _quotaRowId: quotaRowId });
    } catch (err) {
      console.error('Error fetching remaining quota:', err);
      setRemainingQuota(null);
    }
    setLoadingQuota(false);
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
  };

  // Open the partial approval modal for any order type
  const handleApproveClick = (order) => {
    setApprovedQty(String(order?.qtyRequired));
    setQtyError('');
    setTruckInput({ vehicleNumber: '', transporterRate: '', capacityMT: '' });
    setFactoryRate('');
    setFactoryBank('');
    setFactoryUtr('');
    setApprovalModal({ open: true, order });
    fetchRemainingQuota(order);
    if (isSugarTruck(order?.bookingType)) {    
      fetchAvailableVehicles();
    }
  };

  const closeApprovalModal = () => {
    setApprovalModal({ open: false, order: null });
    setApprovedQty('');
    setQtyError('');
    setRemainingQuota(null);
    setFactoryRate('');
    setFactoryBank('');
    setFactoryUtr('');
  };
  
  // Open reject modal
  const handleRejectClick = (order) => {
    setRejectReason('');
    setRejectModal({ open: true, order });
  };

  const closeRejectModal = () => {
    setRejectModal({ open: false, order: null });
    setRejectReason('');
  };

  // Reject order handler
  const handleReject = async () => {
    if (!rejectModal?.order || rejecting) return;
    setRejecting(true);
    try {
      const order = rejectModal?.order;
      const updateData = { status: 'rejected' };
      if (rejectReason?.trim()) {
        updateData.rejection_reason = rejectReason?.trim();
      }
      const { error } = await supabase
        ?.from('buyer_bookings')
        ?.update(updateData)
        ?.eq('id', order?.id);
      if (error) throw error;

      // Revert any assigned trucks for this booking back to 'Available'
      const { data: assignedTrucks } = await supabase
        ?.from('order_truck_assignments')
        ?.select('vehicle_number')
        ?.eq('booking_id', order?.id);
      const vehicleNumbers = (assignedTrucks || [])
        ?.map(r => r?.vehicle_number?.trim()?.toUpperCase())
        ?.filter(v => v && v !== '');
      if (vehicleNumbers?.length > 0) {
        const { error: revertError } = await supabase
          ?.from('trucks')
          ?.update({ status: 'Available' })
          ?.in('vehicle_number', vehicleNumbers);
        if (revertError) {
          console.error('Error reverting truck status on rejection:', revertError);
        }
      }

      closeRejectModal();
      // Also close approval modal if open for same order
      if (approvalModal?.open && approvalModal?.order?.id === rejectModal?.order?.id) {
        closeApprovalModal();
      }
      showNotification('Order rejected successfully.', 'error');
      loadPendingOrders();
    } catch (err) {
      console.error('Error rejecting order:', err);
      showNotification('Failed to reject order. Please try again.', 'error');
    }
    setRejecting(false);
  };

  // Validate approved quantity input
  const handleApprovedQtyChange = (val) => {
    setApprovedQty(val);
    const num = Number(val);
    if (!val || isNaN(num) || num <= 0) {
      setQtyError('Approved Quantity must be greater than 0.');
    } else if (remainingQuota?.remaining !== null && remainingQuota?.remaining !== undefined && num > remainingQuota?.remaining) {
      setQtyError(`Cannot exceed remaining quota of ${Number(remainingQuota?.remaining)?.toLocaleString('en-IN')} MT.`);
    } else {
      setQtyError('');
    }
  };

  // Handle vehicle selection in approval modal — auto-fill capacity and rate

  const isApprovalDisabledCheck =
    approving ||
    !approvedQty ||
    Number(approvedQty) <= 0 ||
    !!qtyError ||
    !factoryRate ||
    Number(factoryRate) <= 0 ||
    !factoryBank?.trim() ||
    !factoryUtr?.trim();

  const handleApprove = async () => {
    if (!approvalModal?.order || isApprovalDisabledCheck) return;
    setApproving(true);

    const booking = approvalModal?.order;    
    if (!booking?.id) {
      setApproving(false);
      return;
    }

    try {
      // Step 1: Direct quota deduction using quota_id (FK on buyer_bookings)
      if (booking?.quotaId) {
        const { data: currentQuota, error: fetchError } = await supabase
          ?.from('quotas')
          ?.select('id, quantity')
          ?.eq('id', booking?.quotaId)
          ?.maybeSingle();

        if (fetchError) {
          console.error('❌ Quota fetch failed:', fetchError);
          setApproving(false);
          setErrorModal({ show: true, message: 'Failed to fetch quota. Please try again.' });
          return;
        }

        if (currentQuota) {
          if (Number(currentQuota?.quantity) < Number(approvedQty)) {
            setApproving(false);
            setErrorModal({ show: true, message: `Insufficient quota. Available: ${currentQuota?.quantity} MT, Requested: ${approvedQty} MT.` });
            return;
          }
          const newQuantity = Number(currentQuota?.quantity) - Number(approvedQty);
          const newAvailabilityStatus = newQuantity <= 0 ? 'sold_out' : 'available';
          const { error: quotaError } = await supabase
            ?.from('quotas')
            ?.update({ quantity: newQuantity, availability_status: newAvailabilityStatus })
            ?.eq('id', booking?.quotaId);

          if (quotaError) {
            console.error('❌ Quota deduction failed:', quotaError);
            setApproving(false);
            setErrorModal({ show: true, message: 'Failed to deduct quota. Please try again.' });
            return;
          }
        }
      } else {
        // Fallback: use offer_id if quota_id not available
        const { data: currentQuota, error: fetchError } = await supabase
          ?.from('quotas')
          ?.select('id, quantity')
          ?.eq('offer_id', booking?.offerId || booking?.offer_id)
          ?.maybeSingle();

        if (fetchError) {
          console.error('❌ Quota fetch failed (fallback):', fetchError);
          setApproving(false);
          setErrorModal({ show: true, message: 'Failed to fetch quota. Please try again.' });
          return;
        }

        if (currentQuota) {
          if (Number(currentQuota?.quantity) < Number(approvedQty)) {
            setApproving(false);
            setErrorModal({ show: true, message: `Insufficient quota. Available: ${currentQuota?.quantity} MT, Requested: ${approvedQty} MT.` });
            return;
          }
          const newQuantity = Number(currentQuota?.quantity) - Number(approvedQty);
          const newAvailabilityStatus = newQuantity <= 0 ? 'sold_out' : 'available';
          const { error: quotaError } = await supabase
            ?.from('quotas')
            ?.update({ quantity: newQuantity, availability_status: newAvailabilityStatus })
            ?.eq('id', currentQuota?.id);

          if (quotaError) {
            console.error('❌ Quota deduction failed (fallback):', quotaError);
            setApproving(false);
            setErrorModal({ show: true, message: 'Failed to deduct quota. Please try again.' });
            return;
          }
        }
      }

      // Step 2: Update the booking record
      // For multi-truck: use first vehicle from vehicleAssignments; for single: use truckInput
  

      const updateData = {
        status: 'approved',
        approved_by: 'quotamanager@sugarhathi.com',
        approved_at: new Date()?.toISOString(),
        factory_rate_per_ton: Number(factoryRate),
        factory_payment_bank: factoryBank?.trim(),
        factory_payment_utr: factoryUtr?.trim(),
      };
      const { error: updateError } = await supabase?.from('buyer_bookings')?.update(updateData)?.eq('id', booking?.id);

      if (updateError) throw updateError;

      // Vehicle assignment handled exclusively by TruckAssignmentModal (post-approval)

      // Step 3: Re-fetch quota live and update remainingQuota state immediately (before closing modal)
      try {
        // Re-fetch current quota quantity (now reduced by the deduction above)
        let updatedQuotaQty = null;
        if (booking?.quotaId) {
          const { data: freshQuota } = await supabase
            ?.from('quotas')
            ?.select('quantity')
            ?.eq('id', booking?.quotaId)
            ?.single();
          if (freshQuota) updatedQuotaQty = Number(freshQuota?.quantity);
        }
        if (updatedQuotaQty === null && booking?.offerId) {
          const { data: freshQuotaRows } = await supabase
            ?.from('quotas')
            ?.select('quantity')
            ?.eq('offer_id', booking?.offerId)
            ?.limit(1);
          if (freshQuotaRows?.length > 0) updatedQuotaQty = Number(freshQuotaRows?.[0]?.quantity);
        }
        if (updatedQuotaQty === null) {
          const { data: freshQuotaRows } = await supabase
            ?.from('quotas')
            ?.select('quantity')
            ?.eq('factory_name', booking?.factoryName)
            ?.eq('grade', booking?.grade)
            ?.limit(1);
          if (freshQuotaRows?.length > 0) updatedQuotaQty = Number(freshQuotaRows?.[0]?.quantity);
        }

        // Re-fetch all approved bookings for this offer (now includes the just-approved one)
        let newAlreadyApproved = 0;
        if (booking?.offerId) {
          const { data: approvedRows } = await supabase
            ?.from('buyer_bookings')
            ?.select('quantity')
            ?.eq('offer_id', booking?.offerId)
            ?.in('status', ['approved', 'active', 'confirmed', 'do_generated'])            
          if (approvedRows) {
            newAlreadyApproved = approvedRows?.reduce((sum, r) => sum + Number(r?.quantity || 0), 0);
          }
        }

        if (updatedQuotaQty !== null) {
          const newFactoryQuota = updatedQuotaQty + newAlreadyApproved;
          const newRemaining = Math.max(0, updatedQuotaQty);
          setRemainingQuota(prev => ({
            ...prev,
            available: newFactoryQuota,
            alreadyApproved: newAlreadyApproved,
            remaining: newRemaining,
          }));
        }
      } catch (quotaRefreshErr) {
        // Non-blocking — don't fail approval if quota refresh fails
        console.warn('Post-approval quota refresh failed (non-blocking):', quotaRefreshErr);
      }

      showNotification(`Order ${booking?.poNumber || booking?.id} approved successfully!`);

      // Auto-generate DO if factory rate + UTR provided (Sugar Only only — Sugar+Truck DO generated at truck assignment)
      if (factoryRate && factoryUtr && !isSugarTruck(booking?.bookingType)) {
        try {
          const { data: doData, error: doErr } = await supabase.functions.invoke('generate-do-pdf-v2', {
            body: {
              booking_id: booking?.readableBookingId,
              factory_rate_per_ton: Number(factoryRate),
              factory_payment_utr: factoryUtr,
              factory_payment_bank: factoryBank?.trim(),
            },
          });
          if (doData?.success) {
            showNotification(`DO generated: ${doData?.do_number}`, 'success');
          } else {
            console.error('DO generation failed:', doErr || doData?.error);
          }
        } catch (doErr) {
          console.error('DO generation error:', doErr);
        }
      }
      // Sugar Only — VAHAN check + approval notification (fire and forget)
if (!isSugarTruck(booking?.bookingType) && booking?.assignedVehicle) {
  supabase.functions.invoke('sugar-approval-notify', {
    body: {
      vehicle_number: booking?.assignedVehicle,
      buyer_email: booking?.buyer,
      booking_id: booking?.id,
      factory_name: booking?.factoryName,
      grade: booking?.sugarGrade,
      quantity: Number(approvedQty),
      rate_per_ton: booking?.ratePerTon,
      total_amount: booking?.totalAmount,
      destination: booking?.deliveryDestination,
    },
  }).then(({ error }) => {
    if (error) console.error('sugar-approval-notify error:', error);
  }).catch(err => console.error('sugar-approval-notify fire error:', err));
}
      closeApprovalModal();

      if (isSugarTruck(booking?.bookingType)) {
        // Always open assignment modal for Sugar+Truck orders (handles capacity-based distribution)
        setAssignModal({ open: true, order: booking });
      }

      // Refresh the list
      loadPendingOrders();
    } catch (err) {
      console.error('Approve error:', err);
      showNotification('Failed to approve order. Please try again.', 'error');
    }
    setApproving(false);
  };

  const isApproveDisabled = isApprovalDisabledCheck;

  const formatAmount = (amount) => '₹' + Number(amount)?.toLocaleString('en-IN');

  // ADD after line 524 (after formatAmount):
  const filteredOrders = useMemo(() => {
    const { from, to } = getDateRange(datePreset, customFrom, customTo);
    const q = searchTerm.trim().toLowerCase();
    return pendingOrders.filter(order => {
      if (q && !(
        order.factoryName?.toLowerCase().includes(q) ||
        order.sugarGrade?.toLowerCase().includes(q) ||
        order.buyer?.toLowerCase().includes(q) ||
        order.poNumber?.toLowerCase().includes(q)
      )) return false;
      if (bookingTypeFilter === 'sugar_only' && isSugarTruck(order.bookingType)) return false;
      if (bookingTypeFilter === 'sugar_truck' && !isSugarTruck(order.bookingType)) return false;
      if (from || to) {
        const d = new Date(order.submittedAt || order.requestedDate);
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      return true;
    });
  }, [pendingOrders, searchTerm, bookingTypeFilter, datePreset, customFrom, customTo]);

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification?.show && (
        <div
          className="fixed top-4 right-4 z-50 flex items-center gap-2 px-5 py-3 rounded-lg shadow-xl text-sm font-medium text-white"
          style={{ background: notification?.type === 'success' ? '#059669' : '#dc2626' }}
        >
          <Icon name={notification?.type === 'success' ? 'CheckCircle' : 'XCircle'} size={18} color="#ffffff" />
          {notification?.message}
        </div>
      )}
      {/* ── Detailed Truck Assignment Modal (post-approval for Sugar+Truck) ── */}
      {assignModal?.open && (
        <TruckAssignmentModal
          order={assignModal?.order}
          onClose={() => setAssignModal({ open: false, order: null })}
          onDone={() => {
            setAssignModal({ open: false, order: null });
            showNotification('Truck assignments saved successfully!', 'success');
          }}
        />
      )}
      {/* ── Reject Confirmation Modal ── */}
      {rejectModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: '#fee2e2' }}>
                  <Icon name="XCircle" size={18} color="#dc2626" />
                </div>
                <div>
                  <h3 className="text-lg font-bold" style={{ color: '#1f2937' }}>Reject Order</h3>
                  <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>This action cannot be undone</p>
                </div>
              </div>
              <button onClick={closeRejectModal} className="p-1.5 rounded-lg hover:bg-gray-100">
                <Icon name="X" size={18} color="#6b7280" />
              </button>
            </div>

            {/* Order Summary */}
            <div className="rounded-xl p-4 space-y-2" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#991b1b' }}>Order to Reject</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs" style={{ color: '#7f1d1d' }}>
                <div><span className="font-semibold">Offer ID:</span> {rejectModal?.order?.poNumber}</div>
                <div><span className="font-semibold">Buyer:</span> {rejectModal?.order?.buyer}</div>
                <div><span className="font-semibold">Grade:</span> {rejectModal?.order?.sugarGrade}</div>
                <div><span className="font-semibold">Qty:</span> {Number(rejectModal?.order?.qtyRequired)?.toLocaleString('en-IN')} MT</div>
              </div>
            </div>

            {/* Rejection Reason */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold" style={{ color: '#374151' }}>
                Rejection Reason <span className="font-normal text-xs" style={{ color: '#9ca3af' }}>(optional)</span>
              </label>
              <textarea
                rows={3}
                value={rejectReason}
                onChange={e => setRejectReason(e?.target?.value)}
                placeholder="e.g. Quota not available for this grade, buyer KYC pending, etc."
                className="w-full px-4 py-2.5 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-red-300 resize-none"
                style={{ border: '1px solid #fca5a5', color: '#1f2937' }}
              />
              <p className="text-xs" style={{ color: '#9ca3af' }}>
                The reason will be recorded against this order for audit purposes.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-1">
              <button
                onClick={closeRejectModal}
                disabled={rejecting}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all"
                style={{ border: '1px solid #d1d5db', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejecting}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
                style={{
                  background: rejecting ? '#d1d5db' : '#dc2626',
                  color: rejecting ? '#9ca3af' : '#ffffff',
                  cursor: rejecting ? 'not-allowed' : 'pointer',
                }}
              >
                {rejecting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Rejecting…
                  </>
                ) : (
                  <>
                    <Icon name="XCircle" size={14} />
                    Confirm Reject
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Partial Approval Modal ── */}
      {approvalModal?.open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm px-0 sm:px-4">
          <div className="bg-white w-full sm:rounded-2xl sm:max-w-xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh] overflow-hidden">

            {/* ── Modal Header ── */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} color="#059669" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">Approve Order</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Review details and confirm approval</p>
                </div>
              </div>
              <button
                onClick={closeApprovalModal}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            {/* ── Scrollable Body ── */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

              {/* Order Summary Card */}
              <div className="rounded-xl border border-gray-100 bg-gray-50 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-100 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Order Summary</p>
                </div>
                <div className="px-4 py-3 grid grid-cols-2 gap-x-6 gap-y-2.5">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Offer ID</p>
                    <p className="text-sm font-semibold text-gray-800 font-mono">{approvalModal?.order?.poNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Buyer</p>
                    <p className="text-sm font-semibold text-gray-800 truncate">{approvalModal?.order?.buyer}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Sugar Grade</p>
                    <p className="text-sm font-semibold text-gray-800">{approvalModal?.order?.sugarGrade}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Factory</p>
                    <p className="text-sm font-semibold text-gray-800 truncate">{approvalModal?.order?.factoryName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Ordered Qty</p>
                    <p className="text-sm font-bold text-gray-900">{Number(approvalModal?.order?.qtyRequired)?.toLocaleString('en-IN')} MT</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Order Type</p>
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${isSugarTruck(approvalModal?.order?.bookingType) ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                      {isSugarTruck(approvalModal?.order?.bookingType) ? '🚛 Sugar + Truck' : '🍬 Sugar Only'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quota Status Card */}
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 overflow-hidden">
                <div className="px-4 py-2.5 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
                  <Icon name="BarChart2" size={14} color="#059669" />
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Quota Status</p>
                </div>
                <div className="px-4 py-3">
                  {loadingQuota ? (
                    <div className="flex items-center gap-2 py-2">
                      <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                      <span className="text-xs text-gray-500">Calculating remaining quota…</span>
                    </div>
                  ) : remainingQuota ? (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center rounded-lg py-2 px-1" style={{ background: '#dcfce7' }}>
                        <p className="text-xs" style={{ color: '#166534' }}>Factory Quota</p>
                        <p className="text-sm font-bold" style={{ color: '#15803d' }}>
                          {remainingQuota?.available !== null ? `${Number(remainingQuota?.available)?.toLocaleString('en-IN')} MT` : '—'}
                        </p>
                      </div>
                      <div className="text-center rounded-lg py-2 px-1" style={{ background: '#fef9c3' }}>
                        <p className="text-xs" style={{ color: '#854d0e' }}>Already Approved</p>
                        <p className="text-sm font-bold" style={{ color: '#a16207' }}>
                          {Number(remainingQuota?.alreadyApproved)?.toLocaleString('en-IN')} MT
                        </p>
                      </div>
                      <div
                        className="text-center rounded-lg py-2 px-1"
                        style={{
                          background: remainingQuota?.remaining <= 0 ? '#fee2e2' : '#d1fae5',
                        }}
                      >
                        <p className="text-xs" style={{ color: remainingQuota?.remaining <= 0 ? '#991b1b' : '#065f46' }}>Remaining</p>
                        <p
                          className="text-sm font-bold"
                          style={{ color: remainingQuota?.remaining <= 0 ? '#dc2626' : '#059669' }}
                        >
                          {remainingQuota?.remaining !== null ? `${Number(remainingQuota?.remaining)?.toLocaleString('en-IN')} MT` : '—'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 py-1">Quota information not available. Proceed with caution.</p>
                  )}
                </div>
              </div>

              {/* Approved Quantity Input */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Approved Quantity <span className="text-gray-400 font-normal">(MT)</span>
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max={remainingQuota?.remaining ?? undefined}
                    value={approvedQty}
                    onChange={e => handleApprovedQtyChange(e?.target?.value)}
                    className={`w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all border ${
                      qtyError
                        ? 'border-red-300 bg-red-50 focus:ring-2 focus:ring-red-200' :'border-gray-200 bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100'
                    } text-gray-900 placeholder-gray-400`}
                    placeholder="Enter quantity to approve"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400 pointer-events-none">MT</span>
                </div>
                {qtyError ? (
                  <p className="text-xs font-medium text-red-500 flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} color="#ef4444" />
                    {qtyError}
                  </p>
                ) : (
                  <p className="text-xs text-gray-400">
                    Default is the full ordered quantity. Reduce to approve a partial amount.
                    {remainingQuota?.remaining !== null && remainingQuota?.remaining !== undefined &&
                      ` Max: ${Number(remainingQuota?.remaining)?.toLocaleString('en-IN')} MT`}
                  </p>
                )}
              </div>

              {/* Factory Rate + UTR */}
              <div className="rounded-xl border border-gray-100 bg-gray-50 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-100 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Factory Payment Details <span className="font-normal normal-case text-red-400">(required to approve)</span></p>
                </div>
                <div className="px-4 py-3 space-y-3">
                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-700">
                      Factory Buy Rate / MT (₹) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 36000"
                      min="0"
                      value={factoryRate}
                      onChange={e => setFactoryRate(e?.target?.value)}
                      className="w-full px-4 py-2.5 rounded-xl text-sm border outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                      style={{ border: '1px solid #d1d5db', color: '#1f2937' }}
                    />
                    <p className="text-xs text-gray-400">Internal — what Villgrains pays the factory. Not shown to buyer.</p>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-700">
                      Factory Payment Bank <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. SBI / HDFC / ICICI"
                      value={factoryBank}
                      onChange={e => setFactoryBank(e?.target?.value)}
                      className="w-full px-4 py-2.5 rounded-xl text-sm border outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                      style={{ border: '1px solid #d1d5db', color: '#1f2937' }}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-700">
                      Factory Payment UTR <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. UTIBR62026052679383266"
                      value={factoryUtr}
                      onChange={e => setFactoryUtr(e?.target?.value?.trim())}
                      className="w-full px-4 py-2.5 rounded-xl text-sm border outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                      style={{ border: '1px solid #d1d5db', color: '#1f2937' }}
                    />
                    <p className="text-xs text-gray-400">Enter UTR to trigger automatic DO generation.</p>
                  </div>
                </div>
              </div>
              {/* Quota Deduction Notice */}
              <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-100">
                <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="Info" size={11} color="#d97706" />
                </div>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Approving will deduct{' '}
                  <strong className="font-semibold">{approvedQty ? `${Number(approvedQty)?.toLocaleString('en-IN')} MT` : '—'}</strong>{' '}
                  from the factory's available quota for grade <strong className="font-semibold">{approvalModal?.order?.sugarGrade}</strong>.
                  {Number(approvedQty) < Number(approvalModal?.order?.qtyRequired) && Number(approvedQty) > 0 && (
                    <span className="block mt-1 text-amber-600">
                      Partial approval: {Number(approvedQty)?.toLocaleString('en-IN')} MT of {Number(approvalModal?.order?.qtyRequired)?.toLocaleString('en-IN')} MT ordered.
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* ── Modal Footer ── */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/60 flex items-center gap-3">
              <button
                onClick={closeApprovalModal}
                disabled={approving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const order = approvalModal?.order;
                  closeApprovalModal();
                  handleRejectClick(order);
                }}
                disabled={approving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-red-600 bg-red-50 border border-red-100 hover:bg-red-100 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <Icon name="XCircle" size={14} color="#dc2626" />
                Reject
              </button>
              <button
                onClick={handleApprove}
                disabled={isApproveDisabled}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
                  isApproveDisabled
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm shadow-emerald-200 cursor-pointer'
                }`}
              >
                {approving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Approving…
                  </>
                ) : (
                  <>
                    <Icon name="Check" size={14} />
                    Approve Order
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold" style={{ color: '#1f2937' }}>Pending Orders</h2>
          <p className="text-sm mt-0.5" style={{ color: '#4b5563' }}>
            {loading ? 'Loading…' : `${filteredOrders.length} of ${pendingOrders.length} order${pendingOrders.length !== 1 ? 's' : ''} awaiting approval`}            
          </p>
        </div>
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
          style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}
        >
          <Icon name="Clock" size={15} color="#d97706" />
          Platform Review Required
        </div>
      </div>
      {/* Info Banner */}
      <div
        className="rounded-xl px-5 py-4 flex items-start gap-3"
        style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}
      >
        <Icon name="Info" size={18} color="#d97706" />
        <p className="text-sm" style={{ color: '#92400e' }}>
          Orders below are submitted by buyers and awaiting platform approval. Clicking <strong>Approve</strong> opens a confirmation modal where you can review the remaining quota and set the approved quantity (partial approval supported). For <strong>Sugar + Truck</strong> orders, you can also assign a vehicle during approval.
        </p>
      </div>
      {/* Filters */}
      <div className="space-y-3">
        <DateRangeFilter
          preset={datePreset}
          customFrom={customFrom}
          customTo={customTo}
          onPresetChange={setDatePreset}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Icon name="Search" size={15} color="#9ca3af" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search factory, grade, buyer email, booking ID…"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none focus:ring-2 focus:ring-amber-300"
              style={{ borderColor: '#fde68a', color: '#1f2937' }}
            />
          </div>
          <select
            value={bookingTypeFilter}
            onChange={e => setBookingTypeFilter(e.target.value)}
            className="px-4 py-2.5 rounded-xl border text-sm font-medium outline-none focus:ring-2 focus:ring-amber-300"
            style={{ borderColor: '#fde68a', color: '#374151', minWidth: '160px' }}
          >
            <option value="all">All Types</option>
            <option value="sugar_only">Sugar Only</option>
            <option value="sugar_truck">Sugar + Truck</option>
          </select>
        </div>
      </div>
      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mr-3"></div>
          <p className="text-sm" style={{ color: '#6b7280' }}>Loading pending orders…</p>
        </div>
      )}
      {/* Orders Table */}
      {!loading && filteredOrders.length === 0 ? (      
        <div className="text-center py-16 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <Icon name="ClipboardList" size={40} color="#d1a87a" />
          <p className="mt-3 text-sm font-medium" style={{ color: '#9ca3af' }}>No pending orders at this time</p>
          <p className="text-xs mt-1" style={{ color: '#d1a87a' }}>New orders submitted by buyers will appear here for your approval</p>
        </div>
      ) : !loading && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #fde68a', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#fef3c7' }}>
                  {['Offer ID', 'Factory Name', 'Grade', 'Qty (MT)', 'Rate/Ton', 'Total Amount', 'Buyer Email', 'Booking Type', 'Date', 'Actions']?.map(col => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap"
                      style={{ color: '#92400e', borderBottom: '1px solid #fde68a' }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order, idx) => (                
                  <tr
                    key={order?.id}
                    style={{
                      background: idx % 2 === 0 ? '#ffffff' : '#fffdf7',
                      borderBottom: '1px solid #fef3c7'
                    }}
                  >
                    <td className="px-4 py-3 font-semibold whitespace-nowrap" style={{ color: '#b45309' }}>
                      {order?.poNumber}
                      {order?.doFinanceBidId && (
                        <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: '#d1fae5', color: '#065f46' }}>
                          💰 Bidded
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#374151', maxWidth: '180px' }}>
                      {order?.factoryName}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: '#fef3c7', color: '#92400e' }}
                      >
                        {order?.sugarGrade}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-center" style={{ color: '#1e293b' }}>
                      {Number(order?.qtyRequired)?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 font-medium" style={{ color: '#374151' }}>
                      ₹{Number(order?.ratePerTon)?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 font-semibold whitespace-nowrap" style={{ color: '#d97706' }}>
                      {formatAmount(order?.totalAmount)}
                    </td>
                    <td className="px-4 py-3" style={{ color: '#1e293b' }}>
                      <span className="font-medium text-xs">{order?.buyer}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{
                          background: isSugarTruck(order?.bookingType) ? '#fce7f3' : '#e0f2fe',
                          color: isSugarTruck(order?.bookingType) ? '#9d174d' : '#0369a1'
                        }}
                      >
                        {isSugarTruck(order?.bookingType) ? '🚛 Sugar + Truck' : 'Sugar Only'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#6b7280' }}>
                      {order?.requestedDate}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleApproveClick(order)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90 whitespace-nowrap"
                        style={{ background: '#059669', color: '#ffffff' }}
                      >
                        <Icon name="Check" size={12} />
                        Approve
                      </button>
                      <button
                        onClick={() => handleRejectClick(order)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90 whitespace-nowrap mt-1.5"
                        style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' }}
                      >
                        <Icon name="XCircle" size={12} color="#dc2626" />
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden divide-y" style={{ divideColor: '#fde68a' }}>
            {filteredOrders.map((order) => (            
              <div key={order?.id} className="p-4 space-y-3" style={{ background: '#ffffff' }}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-bold text-sm" style={{ color: '#b45309' }}>{order?.poNumber}</p>
                    <p className="text-sm font-medium mt-0.5" style={{ color: '#1e293b' }}>{order?.buyer}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>{order?.factoryName}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={{ background: '#fef3c7', color: '#92400e' }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#f59e0b' }} />
                      Pending
                    </span>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={{ background: isSugarTruck(order?.bookingType) ? '#fce7f3' : '#e0f2fe', color: isSugarTruck(order?.bookingType) ? '#9d174d' : '#0369a1' }}
                    >
                      {isSugarTruck(order?.bookingType) ? '🚛 S+T' : '🍬 Sugar Only'}
                    </span>
                    {order?.doFinanceBidId && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: '#d1fae5', color: '#065f46' }}
                      >
                        💰 Bidded
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg px-2.5 py-2" style={{ background: '#fdf8f0' }}>
                    <p className="text-xs" style={{ color: '#9ca3af' }}>Grade</p>
                    <p className="text-sm font-semibold" style={{ color: '#374151' }}>{order?.sugarGrade}</p>
                  </div>
                  <div className="rounded-lg px-2.5 py-2" style={{ background: '#fdf8f0' }}>
                    <p className="text-xs" style={{ color: '#9ca3af' }}>Qty (MT)</p>
                    <p className="text-sm font-semibold" style={{ color: '#1e293b' }}>{Number(order?.qtyRequired)?.toLocaleString('en-IN')}</p>
                  </div>
                  <div className="rounded-lg px-2.5 py-2" style={{ background: '#fdf8f0' }}>
                    <p className="text-xs" style={{ color: '#9ca3af' }}>Rate/Ton</p>
                    <p className="text-sm font-semibold" style={{ color: '#374151' }}>₹{Number(order?.ratePerTon)?.toLocaleString('en-IN')}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs" style={{ color: '#9ca3af' }}>Total Amount</p>
                    <p className="text-base font-bold" style={{ color: '#d97706' }}>{formatAmount(order?.totalAmount)}</p>
                  </div>
                  <p className="text-xs" style={{ color: '#6b7280' }}>{order?.requestedDate}</p>
                </div>
                <div className="pt-2 border-t" style={{ borderColor: '#fde68a' }}>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApproveClick(order)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
                      style={{ background: '#059669', color: '#ffffff' }}
                    >
                      <Icon name="Check" size={13} />
                      Approve
                    </button>
                    <button
                      onClick={() => handleRejectClick(order)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
                      style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' }}
                    >
                      <Icon name="XCircle" size={13} color="#dc2626" />
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <ErrorModal
        show={errorModal?.show}
        message={errorModal?.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />
    </div>
  );
};

export default SupplySidePendingOrdersTab;


